/**
 * Downloads a PDF from a given URL and saves it with a specified filename.
 * @param {string} url - The URL of the PDF to download.
 * @param {string} model - The model name to include in the filename.
 * @param {string} refcode - The refcode to include in the filename.
 */
export const handlePlanPdfDownload = (url, model, refcode=null) => {
    if (!url) return

    // Fetch the PDF file from the URL using fetch
    fetch(url)
        .then(response => {
            if (!response.ok) {
                throw new Error('handlePlanPdfDownload -> Network response was not ok')
            }
            return response.blob() // Convert the response into a Blob object
        })
        .then(blob => {
            // Create a URL for the Blob object
            const downloadUrl = URL.createObjectURL(blob)

            // Create a temporary anchor element to initiate download
            const link = document.createElement('a')
            link.href = downloadUrl

            let fileName

            if (refcode) {
                // Set the download filename
                fileName = `${model}-${refcode}.pdf`
            } else {
                const params = new URL(url).searchParams;
                const encodedPdfUrl = params.get("url");
                const pdfUrl = decodeURIComponent(encodedPdfUrl);
                fileName = pdfUrl.substring(pdfUrl.lastIndexOf("/") + 1);
            }
            link.setAttribute('download', fileName)

            // Append the link to the body and trigger the download
            document.body.appendChild(link)
            link.click()

            // Clean up: remove the link and revoke the object URL
            document.body.removeChild(link)
            URL.revokeObjectURL(downloadUrl)
        })
        .catch(error => {
            console.error('handlePlanPdfDownload -> Failed to download the file', error)
        })
}


export const getFormatConfigData = (config, isPlanAccepted, locale, units, planDocumentUrl, refcode) => {
    // console.log('getFormatConfigData: ', config)
    return {
        "config": {
            ...config.configuration,
            isPlanAccepted: isPlanAccepted,
            planDocumentUrl: planDocumentUrl,
            refcode: refcode,
            reference: config.reference,
        },
        "colorId": config.configuration.material && config.configuration.material.color,
        "finish": config.configuration.material && config.configuration.material.finish,
        "brand": getBrand(config),
        "colorName": config.configuration.material && config.configuration.material.colorName,
        "elaboration": getElaborationType(config.configuration.product),
        "ref": config.reference,
        "lang": locale,
        "units": units
    }
}

const getBrand = (config) => {
    return config.configuration.material && config.configuration.material.brand
}

// Mapea el producto del configurador 3D al tipo de elaboración CTS que espera el backend
// (CutToSizeType::getCode(marca, tipo)). Las piezas llegan siempre partidas desde el
// configurador, cada una con su product y su material.brand real:
//   flooring → CTS_FLOOR (soleria), skirting → CTS_SKIRTI (rodapie), cladding → CTS_COVER (revestimiento)
const getElaborationType = (product) => {
    switch (product) {
        case 'showertrays':
            return 'SHOWER_NEW';
        case 'washbasins':
            return 'SINK_NEW';
        case 'flooring':
            return 'CTS_FLOOR';
        case 'skirting':
            return 'CTS_SKIRTI';
        case 'cladding':
            return 'CTS_COVER';
        default:
            return 'CONFIGURABLE_BATH';
    }
}

export const getCheckoutLineId = (checkoutLines, refcode) => {
    if (!refcode) {
        return null
    }

    const matchingLine = checkoutLines && checkoutLines.find(line =>
        line.cutToSize &&
        line.cutToSize.config &&
        line.cutToSize.ref &&
        line.cutToSize.ref === refcode
    )

    return matchingLine ? matchingLine.lineId : null
}

export const getConfigurablePlanPdfUrl = (configurableDrawingList, refcode) => {
    const configurableDrawing = configurableDrawingList && refcode && configurableDrawingList[refcode]

    return configurableDrawing && configurableDrawing.documentUrl
}